import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import useScreenType from '../../hooks/utility/useScreenType';
import { Drawer, Layout, Tag, Tooltip } from 'antd';
import ReduxHooks from '../../store/ReduxHooks';
import { actionCreators } from '../../store/actions';
import { RouteEnum } from '../../routes/routes';
import WebViewCommunicatorResource from '../../services/resources/WebViewCommunicatorResource';
import { useNavigate } from 'react-router-dom';
import collect from 'collect.js';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../providers/AuthProvider';
import { MailOutlined, PhoneOutlined } from '@ant-design/icons';
import ConfirmationModal from '../../modals/ConfirmationModal';
import { operatorLanguages } from '../../services/localization/brandLanguages';
import { brandFromServiceProvider } from '../../services/branding/brandFromServiceProvider';
import ConfigManager from '../../services/api/ConfigManager';

const Item = ({ data, className }) => {
  const navigate = useNavigate();
  const { isMobile } = useScreenType();

  const onClick = () => {
    if (data.onClick) {
      data.onClick();
    } else {
      navigate(data.to);
    }

    if (isMobile) {
      ReduxHooks.dispatch(actionCreators.sidebar.toggle(false));
    }
  };

  const activeRoute = collect(window.location.pathname.slice(1).split('/')).last();

  return (
    <button
      className={classnames(
        classnames(
          'p-none text-gray-600 hover:text-black hover:font-semibold ease-in-out transition-all cursor-pointer duration-150 delay-75',
          className,
          {
            'text-black font-semibold': activeRoute === data.to || `/${activeRoute}` === data.to,
          },
        ),
      )}
      onClick={onClick}
    >
      {data.name}
    </button>
  );
};

const Content = () => {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Navigation:Sidebar' });

  const { logOut } = useAuth();
  const { company } = useSelector((state) => state.company);
  const { service_provider } = company;

  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);

  // What this terminal may switch between: the partner's languages plus the outlet's own
  // configured one. Gated on the partner rather than the outlet's region — the region is
  // geography and carried no partner input — and the outlet language is always in the
  // list, so the switcher can never omit the language the outlet was configured with.
  //
  // The brand is derived here rather than taken from useBrand() so this stays a pure
  // read of the session already in the store: it is the same derivation BrandProvider
  // does, and AuthProvider gates on it identically. No partner yet (before
  // /staff/profile lands) means nothing to offer, so the switcher stays hidden.
  const sessionBrand = brandFromServiceProvider(service_provider, company?.locales);
  const availableLocales = sessionBrand ? operatorLanguages(sessionBrand, company?.language) : [];
  const nextLocale = availableLocales[(availableLocales.indexOf(i18n.language) + 1) % availableLocales.length];

  /**
   * The tap is the only point where the operator states a preference, so it is the only
   * point that persists one. It reaches staff_accounts.lang, so the choice follows them
   * to another terminal; the startup detection and the brand alignment deliberately do
   * not write (see LocalizationManager.saveLanguage).
   */
  function switchLanguage(locale) {
    i18n.changeLanguage(locale);
    ConfigManager.update({ lang: locale });
  }

  const items = collect([
    {
      name: t('active_orders.title'),
      to: RouteEnum.Home,
    },
    {
      name: t('order_history.title'),
      to: RouteEnum.OrderHistory,
    },
    {
      name: t('menu.title'),
      to: RouteEnum.Menu,
    },
    {
      name: t('printer_settings.title'),
      to: RouteEnum.PrinterSettings,
    },
    {
      name: t('order_control.title'),
      to: RouteEnum.OrderControl,
    },
    {
      name: t('busy_mode.title', {
        time: company.busy_mode_minutes,
        count: company.busy_mode_minutes ?? 0,
      }),
      to: RouteEnum.BusyMode,
    },
    {
      name: t('volume.title'),
      onClick: () => WebViewCommunicatorResource.navigate({ screen: 'VolumeSettingsScreen' }),
    },
  ])
    .when(availableLocales.length > 1, (collection) =>
      collection.push({
        name: t('switch_language.title', { language: nextLocale.toUpperCase() }),
        onClick: () => switchLanguage(nextLocale),
      }),
    )
    .toArray();

  return (
    <div className="relative h-full">
      {Boolean(company?.name) && (
        <h6 className="text-lg font-semibold p-sm lg:p-md">
          {company.name}
          {/* The partner's test restaurant takes real orders; staff should know which ones are tests. */}
          {company.is_test_restaurant && (
            <Tooltip title={t('test_restaurant.tooltip')}>
              <Tag color="warning" className="ml-xs align-middle" data-testid="test-restaurant-tag">
                {t('test_restaurant.label')}
              </Tag>
            </Tooltip>
          )}
        </h6>
      )}
      <ul className="list-none list-inside p-none">
        {items.map((item) => (
          <li key={item.name} className="border-t border-gray-200">
            <Item className="w-full !p-sm lg:p-md text-left" data={item} />
          </li>
        ))}
      </ul>
      <div className="absolute bottom-0 w-full space-y-xs">
        {Boolean(service_provider?.support_phone_number) && (
          <span className="block text-primary px-sm lg:px-md text-xs lg:text-base">
            <PhoneOutlined /> Tel-Support: {service_provider.support_phone_number}
          </span>
        )}
        {Boolean(service_provider?.support_email) && (
          <span className="block text-primary px-sm lg:px-md text-xs lg:text-base">
            <MailOutlined /> Email-Support: {service_provider.support_email}
          </span>
        )}
        <div className="border-t border-gray-200">
          <Item
            className="w-full !p-sm lg:p-md text-left"
            data={{ name: t('logout.title'), onClick: () => setShowLogoutConfirmation(true) }}
          />
        </div>
      </div>
      <ConfirmationModal
        description={t('logout.description')}
        show={showLogoutConfirmation}
        cancelButtonText={t('logout.buttons.cancel')}
        confirmationButtonText={t('logout.buttons.confirm')}
        onConfirm={logOut}
        onCancel={() => setShowLogoutConfirmation(false)}
      />
    </div>
  );
};

export default function Sidebar() {
  const { show } = useSelector((state) => state.sidebar);
  const { isMobile } = useScreenType();

  return isMobile ? (
    <>
      <Drawer
        title={null}
        placement="left"
        open={show}
        onClose={() => ReduxHooks.dispatch(actionCreators.sidebar.toggle(false))}
        size={250}
        styles={{
          header: { display: 'none' },
          body: { padding: 0 },
        }}
      >
        <Content />
      </Drawer>
    </>
  ) : (
    <Layout.Sider collapsed={!show} collapsedWidth={0} width={350} theme="light" className="sticky top-0 h-screen">
      <Content />
    </Layout.Sider>
  );
}
